import { Link, NavLink } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';

const NAV = [
  { label: 'How it works', to: '/how-it-works' },
  { label: 'Economy', to: '/economy' },
  { label: 'For agents', to: '/for-agents' },
  { label: 'For nodes', to: '/for-nodes' },
  { label: 'Contracts', to: '/contracts' },
];

export function SiteShell({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative min-h-screen bg-essence-paper font-sans text-essence-ink">
      <div className="essence-grain fixed inset-0 z-0" aria-hidden />

      <header className="relative z-40 border-b border-essence-line/80 bg-essence-paper/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="font-display text-lg font-bold tracking-tight text-essence-ink sm:text-xl">
            Essence
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1.5 text-sm transition ${
                    isActive
                      ? 'bg-essence-ink/5 text-essence-ink'
                      : 'text-essence-mute hover:bg-essence-ink/5 hover:text-essence-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="https://selflabs.xyz"
              className="hidden text-sm text-essence-mute transition hover:text-essence-teal sm:inline"
            >
              Self Labs
            </a>
            <button
              type="button"
              className="inline-flex rounded-md border border-essence-line p-2 text-essence-ink md:hidden"
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open ? (
          <nav className="border-t border-essence-line px-4 py-3 md:hidden">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2.5 text-sm ${
                    isActive ? 'bg-essence-ink/5 text-essence-ink' : 'text-essence-mute'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a href="https://selflabs.xyz" className="mt-1 block rounded-md px-3 py-2.5 text-sm text-essence-mute">
              Self Labs →
            </a>
          </nav>
        ) : null}
      </header>

      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 border-t border-essence-line bg-essence-surface/80">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-display text-xl font-bold text-essence-ink">Essence</p>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-essence-mute">
                Self Labs / Role Model stablecoin on Base — node rewards, agent payments for goods and services,
                treasury rail toward USDC. Not a meme coin. Not an ungated mint.
              </p>
            </div>
            <div className="flex flex-col gap-2 text-sm text-essence-mute">
              <Link
                to={{ pathname: '/', hash: 'get-the-app' }}
                className="font-semibold text-essence-teal hover:text-essence-gold"
              >
                Download Self OS beta
              </Link>
              <a href="https://selflabs.xyz" className="hover:text-essence-teal">
                selflabs.xyz
              </a>
              <Link to="/contracts" className="hover:text-essence-teal">
                Live contracts
              </Link>
              <a
                href="https://rolemodel.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-essence-teal"
              >
                Privacy
              </a>
              <a
                href="https://rolemodel.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-essence-teal"
              >
                Terms
              </a>
              <a href="https://selflabs.xyz/essence-disclaimer" className="hover:text-essence-teal">
                Disclaimer
              </a>
            </div>
          </div>
          <p className="mt-8 text-xs leading-relaxed text-essence-mute/90">
            Essence is marketed as a stablecoin for rewards and agent payments. Agents can transfer Essence on Base;
            payment for goods and services is an intended use, not a claim that a marketplace is live or that all
            merchants accept it. Peg is notional (≈ $1). EssenceTreasury is an ops-funded vault for redeemability
            toward USDC — not a regulated stablecoin, not FDIC-insured, and not guaranteed fully funded today. On-chain mint requires
            treasury funding and Concierge-signed withdraw — phones do not mint. Mint is admin/bridge-controlled,
            not a fair launch. Supply may be zero. Self OS Android beta is a prototype sideload; compute may use
            battery when opted in.
          </p>
        </div>
      </footer>
    </div>
  );
}
